import { Component } from '@theme/component';

/**
 * @typedef {Object} FAQRefs
 * @property {HTMLElement[]} categoryLabels
 * @property {HTMLAnchorElement[]} categoryLinks
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<FAQRefs>} */
class FAQComponent extends Component {
  #observer = new MutationObserver(() => this.#syncCategoryLabels());
  #onHashChange = () => this.#activateFromHash();

  connectedCallback() {
    super.connectedCallback();
    this.#syncCategoryLabels();
    this.#activateFromHash();
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
    window.addEventListener('hashchange', this.#onHashChange);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncCategoryLabels();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    window.removeEventListener('hashchange', this.#onHashChange);
  }

  #syncCategoryLabels() {
    const { categoryLabels, panels } = this.refs;
    if (!categoryLabels || !panels) return;

    for (let i = 0; i < panels.length; i++) {
      const label = categoryLabels[i];
      const panel = panels[i];
      if (!label || !panel) continue;

      const text = panel.querySelector('.faq-category')?.getAttribute('data-category-name')?.trim();
      if (!text || label.textContent?.trim() === text) continue;

      label.textContent = text;
    }
  }

  #activateFromHash() {
    const { panels } = this.refs;
    if (!panels || panels.length === 0) return;
    const hash = location.hash.slice(1);
    const match = hash ? panels.find((p) => p.id === hash) : null;
    this.#activatePanel(match ?? panels[0]);
  }

  /** @param {HTMLElement | undefined} panel */
  #activatePanel(panel) {
    const { panels, categoryLinks } = this.refs;
    if (!panels || !panel) return;

    for (const p of panels) {
      p.classList.toggle('faq__panel--active', p === panel);
    }

    if (categoryLinks) {
      for (const link of categoryLinks) {
        link.classList.toggle('faq__category-link--active', link.getAttribute('href') === `#${panel.id}`);
      }
    }
  }

  /** @param {MouseEvent} event */
  handleCategoryClick(event) {
    event.preventDefault();
    const target = /** @type {HTMLAnchorElement} */ (event.target);
    const href = target.getAttribute('href') ?? '';
    const panelId = href.startsWith('#') ? href.slice(1) : href;
    const panel = this.refs.panels?.find((p) => p.id === panelId);
    if (!panel) return;
    this.#activatePanel(panel);
    history.replaceState(null, '', href);
  }
}

if (!customElements.get('faq-component')) {
  customElements.define('faq-component', FAQComponent);
}
